<template>
  <div class="cashbox-searcher container container_30">
    <div class="cashbox-searcher__row">
      <div class="cashbox-searcher__filter filter-asset">
        <select-field
          v-model="form.tokenCode"
          name="asset"
          title="Filter requests by asset code"
          :label="i18n.lbl_asset()"
          :values="tokenCodes"
          :clearable="true"
          @clear="onClearSelection('form.tokenCode')"
        />
      </div>
      <div class="cashbox-searcher__filter filter-state">
        <select-field
          v-model="form.state"
          name="state"
          title="Filter requests by status"
          :label="i18n.lbl_status()"
          :values="requestStates"
          :clearable="true"
          @clear="onClearSelection('form.state')"
        />
      </div>
      <div class="cashbox-searcher__filter filter-requestor">
        <input-field
          v-model="form.requestor"
          name="requestor"
          title="Type requestor email or account id"
          :label="i18n.lbl_requestor_id_or_email()"
        />
      </div>
    </div>

    <div class="cashbox-searcher__row">
      <div class="cashbox-searcher__filter filter-date">
        <date-field-flatpickr
          v-model="form.startDate"
          name="cashbox-start-date"
          title="Choose after this date"
          :disableAfter="form.endDate"
          :label="i18n.lbl_start_date()"
          :clearable="true"
          @clear="onClearDate('form.startDate')"
        />
      </div>
      <div class="cashbox-searcher__filter filter-date">
        <date-field-flatpickr
          v-model="form.endDate"
          name="cashbox-end-date"
          title="Choose before this date"
          :disableBefore="form.startDate"
          :label="i18n.lbl_end_date()"
          :clearable="true"
          @clear="onClearDate('form.endDate')"
        />
      </div>
    </div>

  </div>
</template>

<script>
  import { i18n } from '@js/i18n'
  import FormMixin from '@com/mixins/form.mixin'
  import SelectField from '@com/fields/SelectField'
  import InputField from '@com/fields/InputField'
  import FlatPickr from '@com/fields/DateFieldFlatpickr'

  import { mapGetters } from 'vuex'
  import { vuexTypes } from '@vuex/types'
  import { REQUEST_STATES } from '@const'
  // import { DateHelper } from '@js/helpers/date.helper'
  import debounce from 'lodash/debounce'

  const GET_REQUEST_STATES_STR = [
    i18n.withdraw_pending(),
    i18n.withdraw_approved(),
    i18n.withdraw_permanently_rejected()
  ]

  export default {
    name: 'cashbox-searcher',
    mixins: [FormMixin],

    components: {
      InputField, SelectField, FlatPickr
    },

    props: {
      filters: { type: Object, required: false }
    },

    data () {
      return {
        form: {
          state: '',
          tokenCode: '',
          requestor: '',
          startDate: null,
          endDate: null
        },
        i18n,
        REQUEST_STATES
      }
    },
    computed: {
      ...mapGetters([
        vuexTypes.userCashTokens
      ]),
      requestStates () {
        return GET_REQUEST_STATES_STR
      },
      tokenCodes () {
        return this.userCashTokens.map(token => token.code)
      }
    },
    created () {
      if (this.filters) {
        this.form.state = this.getRequestStateText(this.filters.state)
        this.form.tokenCode = this.filters.tokenCode
        this.form.requestor = this.filters.requestor
        this.form.startDate = this.filters.startDate
        this.form.endDate = this.filters.endDate
      }
    },

    methods: {
      getRequestStateCode () {
        if (!this.form.state) return 0
        let stateCode = 0
        switch (this.form.state) {
          case GET_REQUEST_STATES_STR[0]:
            stateCode = REQUEST_STATES.pending
            break
          case GET_REQUEST_STATES_STR[1]:
            stateCode = REQUEST_STATES.approved
            break
          case GET_REQUEST_STATES_STR[2]:
            stateCode = REQUEST_STATES.permanentlyRejected
            break
        }
        return stateCode
      },
      getRequestStateText (code) {
        let stateText = ''
        switch (code) {
          case REQUEST_STATES.pending:
            stateText = GET_REQUEST_STATES_STR[0]
            break
          case REQUEST_STATES.approved:
            stateText = GET_REQUEST_STATES_STR[1]
            break
          case REQUEST_STATES.rejected:
          case REQUEST_STATES.permanentlyRejected:
            stateText = GET_REQUEST_STATES_STR[2]
            break
        }
        return stateText
      },
      composeRequestorFilter () {
        const requestor = this.form.requestor.trim()
        // requestor is full pubkey
        let valid = (requestor.startsWith('G') !== -1 && requestor.length === 56)
        // requestor is full or partially filled email (possible)
        if (!valid) {
          let pos = requestor.indexOf('@')
          if (pos !== -1) {
            pos = requestor.indexOf('.', pos + 1)
            valid = (pos !== -1 && pos < requestor.length - 1)
          }
        }
        // continue filtering
        this.composeFilters(valid)
      },
      formatDateRangeToTimestamps (startDate, endDate) {
        let startTimestamp = ''
        let endTimestamp = ''
        if (startDate) {
          const startDateTime = `${startDate}T00:00:00`
          startTimestamp = Math.floor(new Date(startDateTime).getTime() / 1000)
        }
        if (endDate) {
          const endDateTime = `${endDate}T23:59:59`
          endTimestamp = Math.floor(new Date(endDateTime).getTime() / 1000)
        }
        return {
          startTimestamp,
          endTimestamp
        }
      },
      async composeFilters (validRequestor = false) {
        const state = await this.getRequestStateCode()
        const tokenCode = (await this.form.tokenCode).trim()
        const requestor = (await this.form.requestor).trim()
        const startDate = await this.form.startDate
        const endDate = await this.form.endDate

        if (!validRequestor && state === 0 && tokenCode.length === 0 && requestor.length === 0) {
          // allowed filtering by dates only if both start and end are specified
          if (!startDate || !endDate) {
            this.$emit('clear-view')
            return
          }
        }
        const {startTimestamp, endTimestamp} = this.formatDateRangeToTimestamps(startDate, endDate)
        const filters = {
          asset: tokenCode,
          state: state,
          requestor: requestor,
          created: {
            from: startTimestamp,
            to: endTimestamp
          },
          createdStrings: {
            startDate: startDate,
            endDate: endDate
          }
        }
        console.log(filters)
        this.$emit('compose-filters', filters)
      },
      onClearSelection (fieldName) {
        this[fieldName] = ''
        this.composeFilters()
      },
      onClearDate (fieldName) {
        console.log('clearing:', fieldName)
        this[fieldName] = null
        console.log('check: ', this.form.startDate === null ? 'null' : 'not null')
        this.composeFilters()
      }
    },

    watch: {
      'form.state': debounce(function () { this.composeFilters() }, 1000),
      'form.tokenCode': debounce(function () { this.composeFilters() }, 1000),
      'form.requestor': debounce(function () { this.composeRequestorFilter() }, 1000),
      'form.startDate': debounce(function () { this.composeFilters() }, 1000),
      'form.endDate': debounce(function () { this.composeFilters() }, 1000)
    }
  }
</script>

<style lang="scss" scoped>
  @import '~scss/variables';
  @import '~scss/mixins';

  .cashbox-searcher {

    &__row {
      display: flex;
      justify-content: space-between;
      width: 100%;
    }
  }

  .filter-asset,
  .filter-state,
  .filter-requestor {
    flex: 1 1 30%;
    max-width: 400px;
  }

  .filter-state {
    padding: 0 12px;
  }

  .filter-requestor {
    flex: 2 2 40%;
  }

  .filter-date {
    width: auto;
  }

</style>



// WEBPACK FOOTER //
// src/vue/app/cashbox/index/Cashbox.Searcher.vue